import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import type { AIDefinitionsPlugin, AIDefinitionsPluginConfig } from '../../types';
import { isLivePageViewMode } from '../../utils/api';

import { createPluginState, getPluginState } from './plugin-factory';
import { aiDefinitionsPluginKey } from './plugin-key';
import { getInitialState } from './state';
import { onClickKeywordDecoration } from './util/keyword';
import {
	onMouseoutKeywordStyler,
	onMouseoverKeywordStyler,
	setupKeywordStylerMutationObserver,
} from './util/keyword-styler';

export const createPlugin = (
	{ dispatch }: PMPluginFactoryParams,
	{
		api,
		config,
	}: {
		// api is mandatory but can be undefined
		api: ExtractInjectionAPI<AIDefinitionsPlugin> | undefined;
		config: AIDefinitionsPluginConfig;
	},
) => {
	return new SafePlugin({
		key: aiDefinitionsPluginKey,

		state: createPluginState(dispatch, () => getInitialState()),

		/**
		 * if in the future we want to check whether the initial mode is view mode and initialize the decorations if it's view mode,
		 * we can call `onLivePageModeChange` in `view` to do this
		 */
		view: (editorView: EditorView) => {
			const destroyKeywordStylerMutationObserver = setupKeywordStylerMutationObserver(
				editorView.dom,
			);

			return {
				destroy: () => {
					destroyKeywordStylerMutationObserver();
				},
			};
		},

		props: {
			decorations(state: EditorState) {
				const { keywordDecorations } = getPluginState(state);
				const size = keywordDecorations.find().length;
				if (
					isLivePageViewMode({ api }) &&
					// size check makes sure we expose the experiment only when there are decorations
					// remove this check when remove the experiment platform_editor_live_pages_ai_definitions
					size > 0 &&
					editorExperiment('platform_editor_live_pages_ai_definitions', 'test', { exposure: true })
				) {
					return keywordDecorations;
				}

				return DecorationSet.empty;
			},

			handleDOMEvents: {
				mouseover: (editorView: EditorView, event: MouseEvent) => {
					onMouseoverKeywordStyler(event);
				},

				mouseout: (editorView: EditorView, event: MouseEvent) => {
					onMouseoutKeywordStyler(event);
				},

				click: (editorView: EditorView, event: MouseEvent) => {
					onClickKeywordDecoration({ editorView, event });
				},
			},
		},
	});
};
